import React, { useEffect, useMemo, useState } from 'react';
import {
  PhoneIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import { campaignService } from '../../../services/campaignService';
import { PhoneNumber, PhoneNumberPurpose, PhoneNumberAssignedCarrier } from '../../../types/campaign';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/UI/Card';
import Button from '../../../components/UI/Button';
import { Pagination } from '../../../components/UI/Table';
import toast from 'react-hot-toast';
import TestCallModal from './TestCallModal';
import TelnyxBadge from './TelnyxBadge';
import SectionHeader from '../../../components/UI/SectionHeader';

const ITEMS_PER_PAGE = 10;

const PURPOSE_META: Record<string, { label: string; badge: string }> = {
  manual: { label: 'Manual Dialing', badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  text: { label: 'Text', badge: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  ai: { label: 'AI', badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
  auto: { label: 'Auto Dialing', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  follow_up: { label: 'Follow-Up', badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400' },
  unassigned: { label: 'Unassigned', badge: 'bg-gray-100 text-gray-600 dark:bg-gray-800/40 dark:text-gray-400' },
};

const purposeMeta = (purpose: PhoneNumberPurpose | null | undefined) =>
  PURPOSE_META[purpose ?? 'unassigned'] ?? PURPOSE_META.unassigned;

const PURPOSE_FILTER_KEYS = ['all', 'unassigned', 'manual', 'text', 'ai', 'auto', 'follow_up'] as const;
const normalizeE164 = (n: string) => n.replace(/\s+/g, '');

const CARRIER_OPTIONS: Array<{ value: PhoneNumberAssignedCarrier; label: string }> = [
  { value: 'all', label: 'All Carriers' },
  { value: 'tmobile', label: 'T-Mobile' },
  { value: 'att', label: 'AT&T' },
  { value: 'verizon', label: 'Verizon' },
  { value: 'unknown', label: 'Unknown' },
];

const ManageNumbersTab: React.FC = () => {
  const [numbers, setNumbers] = useState<PhoneNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [purposeFilter, setPurposeFilter] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [vapiImportedNumbers, setVapiImportedNumbers] = useState<Set<string>>(new Set());
  const [importingIds, setImportingIds] = useState<Set<string>>(new Set());
  const [bulkImporting, setBulkImporting] = useState(false);
  const [fixingConnections, setFixingConnections] = useState(false);
  const [testCallNumber, setTestCallNumber] = useState<string | null>(null);

  const loadVapiNumbers = async () => {
    try {
      const vapiNums = await campaignService.getVapiPhoneNumbers();
      setVapiImportedNumbers(new Set(vapiNums.map((n) => normalizeE164(n.number))));
    } catch {
      setVapiImportedNumbers(new Set());
    }
  };

  const loadNumbers = async () => {
    try {
      setLoading(true);
      setError(null);
      const [response] = await Promise.all([campaignService.getPhoneNumbers(), loadVapiNumbers()]);
      setNumbers(response || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load phone numbers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNumbers();
  }, []);

  const handlePurposeChange = async (num: PhoneNumber, value: string) => {
    const next = (value || null) as PhoneNumberPurpose | null;
    const prev = num.purpose ?? null;
    if (next === prev) return;
    setNumbers((cur) => cur.map((n) => (n.id === num.id ? { ...n, purpose: next } : n)));
    try {
      await campaignService.setPhoneNumberPurpose(num.id, next);
      toast.success(`${num.number} → ${purposeMeta(next).label}`);
    } catch (e: any) {
      setNumbers((cur) => cur.map((n) => (n.id === num.id ? { ...n, purpose: prev } : n)));
      toast.error(e?.response?.data?.error?.message || e?.message || 'Failed to update line type');
    }
  };

  const handleCarrierChange = async (num: PhoneNumber, value: string) => {
    const next = (value || 'all') as PhoneNumberAssignedCarrier;
    const prev = num.assignedCarrier || 'all';
    if (next === prev) return;
    setNumbers((cur) => cur.map((n) => (n.id === num.id ? { ...n, assignedCarrier: next } : n)));
    try {
      await campaignService.setPhoneNumberAssignedCarrier(num.id, next);
      const label = CARRIER_OPTIONS.find((c) => c.value === next)?.label || next;
      toast.success(`${num.number} carrier → ${label}`);
    } catch (e: any) {
      setNumbers((cur) => cur.map((n) => (n.id === num.id ? { ...n, assignedCarrier: prev } : n)));
      toast.error(e?.response?.data?.error?.message || e?.message || 'Failed to update carrier');
    }
  };

  const filteredNumbers = useMemo(() => {
    let result = numbers;
    if (purposeFilter !== 'all') {
      const target = purposeFilter === 'unassigned' ? null : purposeFilter;
      result = result.filter((n) => (n.purpose ?? null) === target);
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(
        (num) =>
          (num.number || '').toLowerCase().includes(q) ||
          (num.name || '').toLowerCase().includes(q) ||
          (num.connectionName || '').toLowerCase().includes(q) ||
          purposeMeta(num.purpose).label.toLowerCase().includes(q)
      );
    }
    return result;
  }, [numbers, search, purposeFilter]);

  useEffect(() => setCurrentPage(1), [search, purposeFilter]);

  const purposeCounts = useMemo(() => {
    const counts: Record<string, number> = { all: numbers.length, unassigned: 0, manual: 0, text: 0, ai: 0, auto: 0, follow_up: 0 };
    for (const n of numbers) {
      const key = n.purpose ?? 'unassigned';
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [numbers]);

  const totalPages = Math.max(1, Math.ceil(filteredNumbers.length / ITEMS_PER_PAGE));
  const paginatedNumbers = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredNumbers.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredNumbers, currentPage]);

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allPageSelected = paginatedNumbers.length > 0 && paginatedNumbers.every((n) => selectedIds.has(n.id));

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        paginatedNumbers.forEach((n) => next.delete(n.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        paginatedNumbers.forEach((n) => next.add(n.id));
        return next;
      });
    }
  };

  const handleBulkPurpose = async (value: string) => {
    const next = (value || null) as PhoneNumberPurpose | null;
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkAssigning(true);
    setNumbers((cur) => cur.map((n) => (selectedIds.has(n.id) ? { ...n, purpose: next } : n)));
    let failed = 0;
    await Promise.all(
      ids.map((id) => campaignService.setPhoneNumberPurpose(id, next).catch(() => { failed++; }))
    );
    if (failed > 0) {
      toast.error(`${failed} of ${ids.length} failed — refreshing…`);
      await loadNumbers();
    } else {
      toast.success(`${ids.length} numbers → ${purposeMeta(next).label}`);
    }
    setSelectedIds(new Set());
    setBulkAssigning(false);
  };

  const handleImportToVapi = async (num: PhoneNumber) => {
    if (vapiImportedNumbers.has(normalizeE164(num.number))) return;
    setImportingIds((prev) => new Set(prev).add(num.id));
    try {
      await campaignService.importPhoneNumberToVapi(num.number, num.name || num.number);
      setVapiImportedNumbers((prev) => new Set(prev).add(normalizeE164(num.number)));
      toast.success(`${num.number} imported to VAPI`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.message || 'Failed to import to VAPI');
    } finally {
      setImportingIds((prev) => {
        const next = new Set(prev);
        next.delete(num.id);
        return next;
      });
    }
  };

  const needsConnectionFixCount = useMemo(
    () => numbers.filter((n) => n.needsConnectionFix).length,
    [numbers]
  );

  const handleFixConnections = async () => {
    setFixingConnections(true);
    try {
      const result = await campaignService.fixTelnyxPhoneNumberConnections(false);
      if (result.fixed.length === 0) {
        toast.success('All numbers already on AI-Calling-Agent');
      } else {
        toast.success(
          `Fixed ${result.fixed.length} number${result.fixed.length === 1 ? '' : 's'} → ${result.expectedConnectionName}`
        );
      }
      await loadNumbers();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.message || 'Failed to fix connections');
    } finally {
      setFixingConnections(false);
    }
  };

  const handleBulkImportToVapi = async () => {
    const toImport = numbers.filter(
      (n) => selectedIds.has(n.id) && !vapiImportedNumbers.has(normalizeE164(n.number))
    );
    if (toImport.length === 0) {
      toast.error('No unimported numbers selected');
      return;
    }
    setBulkImporting(true);
    let ok = 0;
    let failed = 0;
    for (const num of toImport) {
      try {
        await campaignService.importPhoneNumberToVapi(num.number, num.name || num.number);
        ok++;
        setVapiImportedNumbers((prev) => new Set(prev).add(normalizeE164(num.number)));
      } catch {
        failed++;
      }
    }
    if (failed > 0) toast.error(`${failed} of ${toImport.length} failed to import`);
    if (ok > 0) toast.success(`${ok} number${ok === 1 ? '' : 's'} imported to VAPI`);
    setSelectedIds(new Set());
    setBulkImporting(false);
  };

  if (loading) {
    return (
      <div className="py-20 flex justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-destructive mb-3">{error}</p>
          <Button variant="outlined" size="sm" onClick={loadNumbers}>Retry</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionHeader
          title="Telnyx Workspace"
          description="Sync numbers, assign line types for dialer channels, and import to VAPI."
          className="mb-0"
        />
        <div className="flex flex-wrap items-center gap-2">
          {needsConnectionFixCount > 0 && (
            <Button variant="outlined" size="sm" loading={fixingConnections} onClick={handleFixConnections}>
              Fix {needsConnectionFixCount} connection{needsConnectionFixCount === 1 ? '' : 's'}
            </Button>
          )}
          <Button variant="outlined" size="sm" onClick={loadNumbers} disabled={loading}>
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Sync from Telnyx
          </Button>
        </div>
      </div>

      {numbers.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-sm flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by number, name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {PURPOSE_FILTER_KEYS.map((key) => {
              const active = purposeFilter === key;
              const label = key === 'all' ? 'All' : PURPOSE_META[key]?.label ?? key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setPurposeFilter(key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border hover:bg-muted'
                  }`}
                >
                  {label}
                  <span className="text-[10px] opacity-70">{purposeCounts[key] ?? 0}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              {search.trim() ? `Showing ${filteredNumbers.length} of ${numbers.length}` : `Telnyx Numbers (${numbers.length})`}
            </CardTitle>
            {filteredNumbers.length > 0 && (
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <input type="checkbox" checked={allPageSelected} onChange={toggleSelectAll} className="h-4 w-4 rounded" />
                Select page
              </label>
            )}
          </div>
        </CardHeader>

        {selectedIds.size > 0 && (
          <div className="mx-6 mb-3 flex items-center gap-3 rounded-lg bg-primary/5 border border-primary/20 px-4 py-2.5 flex-wrap">
            <span className="text-sm font-medium">{selectedIds.size} selected</span>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <Button variant="outlined" size="sm" loading={bulkImporting} disabled={bulkAssigning} onClick={handleBulkImportToVapi}>
                <ArrowUpTrayIcon className="h-3.5 w-3.5 mr-1" />
                Import to VAPI
              </Button>
              <select
                disabled={bulkAssigning}
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) {
                    handleBulkPurpose(e.target.value === '__unassigned' ? '' : e.target.value);
                    e.target.value = '';
                  }
                }}
                className="text-xs border rounded-md px-2 py-1.5"
              >
                <option value="" disabled>Bulk assign…</option>
                <option value="__unassigned">Unassigned</option>
                <option value="manual">Manual Dialing</option>
                <option value="text">Text</option>
                <option value="ai">AI</option>
                <option value="auto">Auto Dialing</option>
                <option value="follow_up">Follow-Up</option>
              </select>
              <button type="button" onClick={() => setSelectedIds(new Set())} className="text-xs text-muted-foreground hover:text-foreground">
                Clear
              </button>
            </div>
          </div>
        )}

        <CardContent>
          {numbers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No phone numbers found. Connect Telnyx in Settings, then sync.
            </p>
          ) : filteredNumbers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No numbers match your search.</p>
          ) : (
            <>
              <div className="space-y-3">
                {paginatedNumbers.map((num) => (
                  <div
                    key={num.id}
                    className={`rounded-lg border p-4 flex items-start justify-between gap-4 ${
                      selectedIds.has(num.id) ? 'border-primary/40 bg-primary/5' : 'border-border'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(num.id)}
                        onChange={() => toggleSelect(num.id)}
                        className="mt-1 h-4 w-4 rounded cursor-pointer"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setTestCallNumber(num.number)}
                            title="Test call"
                            className="p-1 rounded-md hover:bg-primary/10"
                          >
                            <PhoneIcon className="h-4 w-4 text-primary" />
                          </button>
                          <p className="font-medium font-mono">{num.number}</p>
                          <TelnyxBadge className="mt-1" />
                        </div>
                        {num.name && num.name !== num.number && (
                          <p className="text-sm text-muted-foreground mt-1">{num.name}</p>
                        )}
                        {num.connectionName && (
                          <p className={`text-xs mt-1 ${num.connectionOk ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-400 font-medium'}`}>
                            {num.connectionOk ? 'Connection: ' : 'Wrong connection: '}
                            {num.connectionName}
                            {!num.connectionOk && ' — use AI-Calling-Agent'}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-xs space-y-1.5">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        {vapiImportedNumbers.has(normalizeE164(num.number)) ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">In VAPI</span>
                        ) : (
                          <Button variant="outlined" size="sm" loading={importingIds.has(num.id)} onClick={() => handleImportToVapi(num)} className="text-xs h-7">
                            <ArrowUpTrayIcon className="h-3.5 w-3.5 mr-1" />
                            VAPI
                          </Button>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${purposeMeta(num.purpose).badge}`}>
                          {purposeMeta(num.purpose).label}
                        </span>
                        <select
                          value={num.purpose ?? ''}
                          onChange={(e) => handlePurposeChange(num, e.target.value)}
                          className="text-xs border rounded-md px-1.5 py-1"
                        >
                          <option value="">Unassigned</option>
                          <option value="manual">Manual</option>
                          <option value="text">Text</option>
                          <option value="ai">AI</option>
                          <option value="auto">Auto</option>
                          <option value="follow_up">Follow-Up</option>
                        </select>
                        <select
                          value={num.assignedCarrier || 'all'}
                          onChange={(e) => handleCarrierChange(num, e.target.value)}
                          className="text-xs border rounded-md px-1.5 py-1"
                          title="Caller ID carrier pool"
                        >
                          {CARRIER_OPTIONS.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <p className="inline-flex items-center gap-1.5 text-muted-foreground">
                        Provider
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="mt-4">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredNumbers.length}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPage}
                    showInfo
                    size="sm"
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {testCallNumber && (
        <TestCallModal fromNumber={testCallNumber} onClose={() => setTestCallNumber(null)} />
      )}
    </div>
  );
};

export default ManageNumbersTab;
